import React from 'react';
import { styled } from '@mui/material/styles';
import { IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps } from '@mui/material';

type IconButtonVariant = 'primary' | 'secondary' | 'destructive';
type IconButtonSize = 'small' | 'medium';

const StyledIconButton = styled(MuiIconButton, {
	shouldForwardProp: (prop) => prop !== 'isActive',
})<{
	buttonvariant?: IconButtonVariant;
	buttonsize?: IconButtonSize;
	isActive?: boolean;
}>(({ buttonvariant = 'secondary', buttonsize = 'medium', isActive = false }) => {
	const sizeConfig = {
		small: {
			width: '32px',
			height: '32px',
			iconSize: '16px',
		},
		medium: {
			width: '40px',
			height: '40px',
			iconSize: '16px',
		},
	};

	const currentSize = sizeConfig[buttonsize];

	return {
		width: currentSize.width,
		height: currentSize.height,
		borderRadius: '8px',
		padding: 0,
		transition: 'all 0.2s ease',
		outline: 'none',
		'&:focus, &:focus-visible': {
			outline: 'none',
		},
		'& svg': {
			width: currentSize.iconSize,
			height: currentSize.iconSize,
		},

		...(buttonvariant === 'primary' && {
			backgroundColor: 'var(--neutral-base-white)',
			color: 'var(--neutral-dark-base)',
			border: '1px solid var(--neutral-outline)',
			...(isActive && {
				backgroundColor: 'var(--primary-100)',
				borderColor: 'var(--primary-500)',
			}),
			'&:hover': {
				backgroundColor: 'var(--primary-100)',
				borderColor: 'var(--neutral-outline)',
			},
			'&:active': {
				backgroundColor: 'var(--primary-100)',
				borderColor: 'var(--primary-500)',
			},
			'&.Mui-disabled': {
				backgroundColor: 'var(--neutral-base-white)',
				borderColor: 'var(--neutral-outline)',
				color: 'var(--neutral-outline)',
			},
			'& .MuiTouchRipple-root .MuiTouchRipple-child': {
				backgroundColor: 'var(--primary-300) !important',
			},
		}),

		...(buttonvariant === 'secondary' && {
			backgroundColor: 'transparent',
			color: 'var(--neutral-dark-base)',
			border: 'none',
			...(isActive && {
				backgroundColor: 'var(--neutral-bg-active)',
				color: 'var(--primary-500)',
			}),
			'&:hover': {
				backgroundColor: 'var(--neutral-bg-active)',
			},
			'&:active': {
				backgroundColor: 'var(--neutral-bg-active)',
				color: 'var(--primary-500)',
			},
			'&.Mui-disabled': {
				backgroundColor: 'transparent',
				color: 'var(--neutral-outline)',
			},
			'& .MuiTouchRipple-root .MuiTouchRipple-child': {
				backgroundColor: 'var(--neutral-bg-active) !important',
			},
		}),

		...(buttonvariant === 'destructive' && {
			backgroundColor: 'var(--neutral-base-white)',
			color: 'var(--error-500)',
			border: '1px solid var(--neutral-outline)',
			...(isActive && {
				backgroundColor: 'var(--error-100)',
				borderColor: 'var(--error-500)',
			}),
			'&:hover': {
				backgroundColor: 'var(--error-100)',
				borderColor: 'var(--error-200)',
			},
			'&:active': {
				backgroundColor: 'var(--error-100)',
				borderColor: 'var(--error-500)',
			},
			'&.Mui-disabled': {
				backgroundColor: 'var(--neutral-base-white)',
				borderColor: 'var(--neutral-outline)',
				color: 'var(--neutral-outline)',
			},
			'& .MuiTouchRipple-root .MuiTouchRipple-child': {
				backgroundColor: 'var(--error-300) !important',
			},
		}),
	};
});

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'size'> {
	variant?: IconButtonVariant;
	size?: IconButtonSize;
	icon: React.ReactNode;
	active?: boolean;
}

export const IconButton: React.FC<IconButtonProps> = ({
	variant = 'secondary',
	size = 'medium',
	icon,
	active = false,
	className = '',
	...props
}) => {
	return (
		<StyledIconButton
			className={className}
			buttonvariant={variant}
			buttonsize={size}
			isActive={active}
			{...props}
		>
			{icon}
		</StyledIconButton>
	);
};

export default IconButton;
